import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import type { PagedListReturn } from '@cks-internal/react-base';
import { useFrontierAuth } from 'src/util/auth/useFrontierAuth';
import { projectDashboardNavQueryKeys } from 'src/features/companyProjects/navigation/api/projectDashboardNavQueryKeys';
import { projectContainerQueryKeys } from 'src/features/companyProjects/projectContainers/api/projectContainerQueryKeys';
import { projectContainerCompanyKeys } from 'src/features/companyProjects/projectContainers/dashboard/sections/company/apis/projectContainerCompanyKeys';
import type { ProjectContainerTableModel } from 'src/features/companyProjects/projectContainers/manageContainers/types/ProjectContainerTableModel';
import { normalizeInboxEvent } from 'src/features/companyProjects/projectIntake/inbox/hubs/ProjectIntakeInboxHubActions';
import { projectIntakeQueryKeys } from 'src/features/companyProjects/projectIntake/api/projectIntakeQueryKeys';
import { projectHotelQueryKeys } from 'src/features/companyProjects/projectContainers/dashboard/sections/hotels/apis/projectHotelQueryKeys';
import {
  applyProjectContainerListEventToTablePage,
  normalizeContainerEvent,
  normalizeFolderEvent,
  normalizeProjectAssessmentEvent,
  normalizeProjectHotelEvent,
  normalizeProjectContainerListEvent,
  type ProjectContainerListEventDto
} from 'src/features/companyProjects/realtime/events/projectContainerEvents';
import { subscribeProjectDashboard } from 'src/features/companyProjects/realtime/dashboard/projectDashboardHub';
import {
  shouldInvalidateAssignedContainersForContainerEvent,
  shouldInvalidateAssignedContainersForInboxEvent,
  useProjectDashboardRealtimeStore
} from 'src/features/companyProjects/realtime/stores/projectDashboardRealtimeStore';

/**
 * Single subscription point: SignalR → Zustand store + React Query invalidations / manage-projects table patches.
 */
export const ProjectDashboardRealtimeBridge = () => {
  const queryClient = useQueryClient();
  const { user, hasLoaded, isAuthenticated } = useFrontierAuth();

  useEffect(() => {
    if (!hasLoaded || !isAuthenticated) {
      return;
    }

    const unsubscribe = subscribeProjectDashboard({
      OnInboxEvent: (...rawArgs: unknown[]) => {
        try {
          const event = normalizeInboxEvent(rawArgs);
          if (!event) return;
          useProjectDashboardRealtimeStore.getState().applyInboxEvent(event);

          if (shouldInvalidateAssignedContainersForInboxEvent(event)) {
            void queryClient.refetchQueries({
              queryKey: projectDashboardNavQueryKeys.myAssignedProjectContainers(),
              type: 'all'
            });
          }
          if (event.intakeId) {
            void queryClient.invalidateQueries({ queryKey: projectIntakeQueryKeys.detail(event.intakeId) });
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      },
      OnContainerEvent: (...args: unknown[]) => {
        const event = normalizeContainerEvent(args);
        if (!event) return;
        useProjectDashboardRealtimeStore.getState().applyContainerEvent(event);
        if (shouldInvalidateAssignedContainersForContainerEvent(event)) {
          void queryClient.refetchQueries({
            queryKey: projectDashboardNavQueryKeys.myAssignedProjectContainers(),
            type: 'all'
          });
        }
      },
      OnFolderEvent: (...args: unknown[]) => {
        const event = normalizeFolderEvent(args);
        if (!event) return;
        useProjectDashboardRealtimeStore.getState().applyFolderEvent(event);
      },
      OnProjectHotelsEvent: (...args: unknown[]) => {
        const event = normalizeProjectHotelEvent(args);
        if (!event) return;

        void queryClient.invalidateQueries({ queryKey: projectHotelQueryKeys.list(event.containerId) });

        if (event.projectHotelId) {
          void queryClient.invalidateQueries({
            queryKey: projectHotelQueryKeys.availableRates(event.containerId, event.projectHotelId)
          });
        }
      },
      OnProjectContainerEvent: (...args: unknown[]) => {
        const [dto] = args as [ProjectContainerListEventDto];
        const event = normalizeProjectContainerListEvent(dto);
        if (!event) return;

        const currentUserId = user?.UserId;
        useProjectDashboardRealtimeStore.getState().applyProjectContainerListEvent(event, currentUserId);

        const tableQueries = queryClient.getQueryCache().findAll({ queryKey: projectContainerQueryKeys.table() });
        for (const query of tableQueries) {
          const key = query.queryKey as readonly unknown[];
          queryClient.setQueryData<PagedListReturn<ProjectContainerTableModel> | undefined>(key, (current) =>
            applyProjectContainerListEventToTablePage(current, event)
          );
        }

        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.edit(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.overview(event.containerId),
          type: 'all'
        });
      },
      OnProjectAssessmentEvent: (...args: unknown[]) => {
        const event = normalizeProjectAssessmentEvent(args);
        if (!event) return;

        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.assessment(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerQueryKeys.edit(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.overview(event.containerId),
          type: 'all'
        });
        void queryClient.invalidateQueries({
          queryKey: projectContainerCompanyKeys.contacts(event.containerId),
          type: 'all'
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [hasLoaded, isAuthenticated, queryClient, user?.UserId]);

  return null;
};
